import useModernCheckout from '~/utils/shop/useModernCheckout';
import buildMagentoCheckoutUrl from '~/utils/shop/buildMagentoCheckoutUrl';
import { useCartStore } from '~/stores/cartStore/cartStore';

export type CheckoutPageType =
  | 'cartPage'
  | 'requestCartPage'
  | 'cartCheckoutPage'
  | 'requestCartCheckoutPage'
  | 'cartConfirmationPage'
  | 'requestCartConfirmationPage';

export type CustomerAccountPageType =
  | 'requestCartConfirmationPage'
  | 'customerAccountAddressesPage'
  | 'customerAccountOrdersPage'
  | 'customerAccountOverviewPage'
  | 'customerAccountProfilePage'
  | 'customerAccountRequestsPage'
  | 'customerAccountOnboardingPreferencesPage';

export type CustomerAreaPageType = CheckoutPageType | CustomerAccountPageType;

function isCheckoutPageType(
  type: CustomerAreaPageType
): type is CheckoutPageType {
  return [
    'cartPage',
    'requestCartPage',
    'cartCheckoutPage',
    'requestCartCheckoutPage',
    'cartConfirmationPage',
    'requestCartConfirmationPage'
  ].includes(type);
}

function isCustomerAccountPageType(
  type: CustomerAreaPageType
): type is CustomerAccountPageType {
  return [
    'customerAccountAddressesPage',
    'customerAccountOrdersPage',
    'customerAccountOverviewPage',
    'customerAccountProfilePage',
    'customerAccountRequestsPage',
    'customerAccountOnboardingPreferencesPage'
  ].includes(type);
}

export default function buildCustomerAreaUrl(
  pageType: CustomerAreaPageType
): string | undefined {
  const { $globalPageSettings, $activeLocale } = useNuxtApp();

  // For China: use Magento shop frontend URLs
  if ($activeLocale.value.webapp === 'zh-cn') {
    const cartStore = useCartStore();

    // Customer account pages always use Magento
    if (isCustomerAccountPageType(pageType)) {
      return cartStore.profileLink;
    }

    // Checkout pages: only use webapp if modern checkout is enabled, otherwise use Magento
    if (isCheckoutPageType(pageType) && !useModernCheckout()) {
      return buildMagentoCheckoutUrl(cartStore.activeCartType);
    }
  }

  const shopConfig = $globalPageSettings.value?.shopConfig;

  const slug = (() => {
    if (isCheckoutPageType(pageType)) {
      if (
        useModernCheckout() &&
        shopConfig &&
        pageType in shopConfig &&
        shopConfig[pageType]
      ) {
        return shopConfig[pageType].slug;
      }
    }

    if (isCustomerAccountPageType(pageType) && shopConfig) {
      const page = shopConfig[pageType];
      if (page) {
        return page.slug ?? undefined;
      }
    }
    const cartStore = useCartStore();
    return buildMagentoCheckoutUrl(cartStore.activeCartType);
  })();

  if (slug) {
    return buildUrlString($activeLocale.value.webapp, slug);
  }
}
